<template>
  <div>
    <Topbar title="Panier" backButton />
    <section class="Section">
      <div class="Section--cart">
        <div class="Section__empty" v-if="cart.lines.length == 0">
          <p>Aucun produit n'a été ajouté au panier.</p>
          <img
            style="max-height: 30vh;"
            src="../assets/images/monkey.png"
            alt="Un singe avec un chapeau de marin tenant une bouteille de vin avec une référence à Walt Whitman…"
          />
          <router-link class="Button Button--primary" :to="{name: 'Offers'}">Voir les offres de la semaine</router-link>
        </div>

        <div class="Cart" v-else>
          <div class="Cart__list">
            <h3>Ma Commande</h3>
            <div v-if="linesByDomain.length > 0">
              <CartDomain
                v-for="domain in linesByDomain"
                :key="domain.producer"
                :domain="domain"
                @remove="removeFromCart"
                @update="modifyQuantity"
              />
            </div>
          </div>
          <div class="Cart__total">
            <div class="Cart__total-btls">
              <label for="">Nombre de bouteilles&nbsp;</label>
              <strong>
                {{ totalChargeableBtls }}
                <span v-if="totalFreeBtls > 0"> + <strong>{{ totalFreeBtls }}</strong> {{ "gratuite" | pluralize(totalFreeBtls) }}</span>
              </strong>
            </div>
            <div class="Cart__total-price">
              <label for="">TOTAL de commande&nbsp;:</label>
              <div class="Cart__total-value">
                <Price :price="totalPrice" />
              </div>
            </div>
            <div class="Cart__disclaimer">Possibilité de frais de port en supplément selon quantité.</div>
          </div>

          <div class="Cart__addresses">
            <AddressesBox
              :addresses="addresses"
              :selectedAddress="selectedAddress"
              @addAddress="addAddress"
              @selectAddress="selectAddress"
            />
          </div>

          <div class="Cart__footer">
            <div class="Cart__comment">
              <label for="comment" class="Cart__comment-label">Ajouter un commentaire à ma commande&nbsp;:</label>
              <textarea
                class="Cart__comment-content"
                name="comment"
                id="comment"
                :value="cart.comment"
                @blur="updateComment"
              ></textarea>
            </div>

            <div class="Cart__action">
              <div @click="$router.back()" class="Button Button--primary">Continuer ma sélection</div>
              <Button class="Button--outline" v-if="availableLines.length > 0" :disabled="disableOrder || selectedAddress === null" @click.native="order">Passer commande</Button>
            </div>

          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import notificationService from "../services/notification";
import addressService from "../services/address";
import CartDomain from "../components/CartDomain";
import AddressesBox from "../components/AddressesBox";
import Button from "../components/Button";

export default {
  name: "Cart",
  data() {
    return {
      disableOrder: false,
      addresses: [],
      selectedAddress: null
    };
  },
  computed: {
    ...mapState("cart", ["cart"]),
    ...mapGetters("cart", ["totalPrice", "totalChargeableBtls", "totalFreeBtls"]),
    availableLines() {
      return this.cart.lines.filter(l => l.availability);
    },
    totalShippingQty() {
      return this.linesByDomain.pari_tenu.reduce((acc, domain) => acc + domain.lines.reduce((acc, line) => acc + line.quantity, 0), 0);
    },
    isShippingFree() {
      return this.totalShippingQty >= 60;
    },
    linesByDomain() {
      return Object.values(this.cart.lines.reduce((acc, line) => {
        const producer = line.producer;

        acc[producer] ? acc[producer].push(line) : acc[producer] = [line]; // eslint-disable-line no-unused-expressions

        return acc;
      }, {})) // group lines by domain and return array of values
        .map(linesByDomain => {
          const { producer, free_shipping_amount, free_shipping_bottles } = linesByDomain[0];

          return {
            producer,
            free_shipping_amount,
            free_shipping_bottles,
            hasFreeShipping: free_shipping_amount || free_shipping_bottles,
            lines: linesByDomain,
            supply: this.cart.suppliers.filter(supply => supply.name === producer)[0]
          };
        })
        .sort((a, b) => a.producer.localeCompare(b.producer));
    }
  },
  methods: {
    removeFromCart(product_id) {
      this.disableOrder = true;

      this.$store.dispatch("cart/removeFromCart", product_id)
        .catch(this.onError)
        .then(() => this.disableOrder = false);
    },
    updateComment(e) {
      this.disableOrder = true;

      this.$store.dispatch("cart/updateComment", e.target.value)
        .catch(this.onError)
        .then(() => this.disableOrder = false);
    },
    modifyQuantity(product_id, oldQuantity, newQuantity) {
      if (oldQuantity === newQuantity) {
        return;
      }

      this.disableOrder = true;

      this.$store.dispatch("cart/modifyQuantity", {
        product_id,
        quantity: newQuantity
      })
        .catch(this.onError)
        .then(() => this.disableOrder = false);
    },
    order() {
      this.disableOrder = true;

      this.$store.dispatch("cart/order", { delivery_address_id: this.selectedAddress?.id })
        .then(() => {
          notificationService.success("Votre commande a bien été envoyée à CaptainVin");
          this.$router.replace({ name: "Orders" });
        })
        .catch(this.onError)
        .then(() => this.disableOrder = false);
    },
    addAddress(address) {
      this.disableOrder = true;

      addressService.post(address)
        .then(({ data: newAddress }) => {
          this.addresses.push(newAddress);
          this.selectedAddress = newAddress;
        })
        .catch(this.onError)
        .finally(() => this.disableOrder = false);
    },
    selectAddress(address) {
      this.selectedAddress = address;
    },
    onError(err) {
      notificationService.error(err.message, err.errors);
    }
  },
  created() {
    this.disableOrder = true;

    addressService.getAll()
      .then(({ data: addresses }) => {
        this.addresses = addresses;

        if (addresses.length > 0) {
          this.selectedAddress = addresses[0];
        }
      })
      .catch(this.onError)
      .finally(() => this.disableOrder = false);
  },
  components: { CartDomain, Button, AddressesBox }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

.Section--cart {
  background-color: #fff;
}

.Cart {
  &__empty-text {
    text-align: center;
    font-size: 1.25em;
    color: $color-grey;
  }

  &__list,
  &__total,
  &__footer,
  &__action,
  &__addresses {
    padding: $global-margin;
  }

  &__list {
    h3 {
      margin: 0 0 $global-margin 0;
    }

    h4,
    h5 {
      margin: $global-margin 0 0 0;
    }
  }

  &__shipping-label {
    font-size: 0.8em;
    font-style: italic;
    color: $color-danger;

    &--success {
      color: $color-success;
    }
  }

  &__comment {
    flex: 1 0 66%;
  }

  &__comment-label {
    font-weight: 600;
    display: block;
    margin-bottom: 0.5em;
  }

  &__comment-content {
    background: none;
    font-family: inherit;
    font-size: inherit;
    margin-bottom: $global-margin;
    width: 100%;
    resize: none;
    min-height: 8rem;
    border-radius: 3px;
    border: 1px solid $color-smoke-border;
  }

  label[for="acceptedTerms"] {
    font-size: 0.875rem;
  }

  &__total {
    background-color: $color-primary;
    padding: $global-margin;
    margin-bottom: $global-margin;
    color: #fff;
  }

  &__total-btls,
  &__total-price {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }

  &__total-btls label {
    font-size: 0.875rem;
  }

  &__total-price {
    margin: 0.5rem 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    label {
      font-weight: 700;
    }
  }

  &__total-value {
    font-size: 1.3125rem;
    text-align: right;
  }

  &__action {
    flex: 1 0 34%;
    display: flex;
    flex-flow: column;
    align-items: center;
    justify-content: center;

    .Button {
      text-align: center;
      margin-bottom: $global-margin;
      width: 100%;
      max-width: 14rem;

      @media (max-width: 400px) {
        width: 100%;
      }
    }
  }

  &__disclaimer {
    font-style: italic;
    font-size: 0.8rem;
  }
}

@media (min-width: 900px) {
  .Cart__footer {
    display: flex;
  }
}

.required-quantity {
  font-weight: 500;
}

</style>
